/**
 * HamburgerMenu.tsx
 * Traditional dropdown menu for admin, profile, and settings
 */
import React, { useEffect, useRef } from 'react';
import { User } from '../../services/auth';
import { Shield } from 'lucide-react';
import { theme } from '@turn-connect/ui';
import { isSystemOrAccountAdmin, useResponsive } from '@turn-connect/hooks';

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  permissions: string[];
  onEditProfile: () => void;
  onAdminDashboard?: () => void;
  onLogout: () => void;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ isOpen, onClose, user, permissions, onEditProfile, onAdminDashboard, onLogout }) => {
  const { isMobile } = useResponsive();
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if user has admin permissions
  const isAdmin = isSystemOrAccountAdmin(permissions) || user?.role === 'super_admin' || user?.role === 'account_admin';

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add slight delay to prevent immediate close on open
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, onClose]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleMenuItemClick = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <>
      {/* Dropdown Menu */}
      <div
        ref={menuRef}
        style={{
          position: 'fixed',
          top: isMobile ? '3.5rem' : '3.75rem', // Below GlobalHeader (its height on a phone / elsewhere)
          left: 16, // Aligned with hamburger button
          minWidth: 220,
          backgroundColor: theme.colors.background.secondary,
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: 8,
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'translateY(0)' : 'translateY(-8px)',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        {/* Menu Items */}
        <div style={{ padding: '8px' }}>
          {/* Platform Administration — the cross-account/system console. Named
              for what it administers, since every account admin also has a
              "dashboard" and the old label read as theirs. */}
          {isAdmin && onAdminDashboard && (
            <MenuItem icon={<Shield size={18} />} label="Platform Administration" onClick={() => handleMenuItemClick(onAdminDashboard)} />
          )}
        </div>
      </div>
    </>
  );
};

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'danger';
}

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, onClick, disabled = false, variant = 'default' }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        backgroundColor: isHovered && !disabled ? 'var(--tc-overlay-washStrong, rgba(255, 255, 255, 0.08))' : 'transparent',
        border: 'none',
        borderRadius: 6,
        color: disabled
          ? theme.colors.text.disabled
          : variant === 'danger'
            ? 'var(--tc-brand-primary, #DA562C)'
            : theme.colors.text.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        fontWeight: 500,
        textAlign: 'left',
        transition: 'background-color 0.15s ease',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}>{icon}</div>
      <span>{label}</span>
    </button>
  );
};

export default HamburgerMenu;
